import { HEN_BREEDS } from '../data/breeds';
import { ACHIEVEMENTS } from '../data/achievements';
import { TRAITS } from '../data/misc';
import { ROOSTER_COSTS } from '../data/balance';
import { setFarmName } from '../core/actions';
import { offlineCapMs } from '../core/economy';
import { formatDuration, formatMoney } from '../core/format';
import type { OfflineReport } from '../core/offline';
import type { GameMessage } from '../core/types';
import { hasLegacySave } from '../platform/storage';
import { track } from '../platform/telemetry';
import { game } from './controller';
import { h } from './dom';
import { fieldFx } from './farmView';
import { closeModal, openModal } from './modals';
import { setTab } from './shop';
import { toast } from './toasts';

export function showIntro(): void {
  const input = h('input', { type: 'text', maxlength: 30, placeholder: 'Sunny Side Farm', 'aria-label': 'Farm name', style: 'width:100%' });
  const start = () => {
    setFarmName(game.state, input.value);
    game.setPrefs({ introDone: true, legacyNoticeShown: true });
    game.changed();
    closeModal();
  };
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') start(); });
  const legacy = hasLegacySave() && !game.prefs.legacyNoticeShown
    ? h('p', { class: 'hint', text: 'Welcome back, farmer. This is Hen House 1.0 — a fresh start on a new engine. Your old farm is still saved in this browser and is left untouched.' })
    : null;
  openModal(h('div', {},
    h('h2', { text: '🐔 Welcome to Hen House' }),
    h('p', { text: 'Build a little flock into a thriving chicken farm — at your own pace.' }),
    legacy,
    h('label', { class: 'meta', text: 'Name your farm' }), input,
    h('ul', {},
      h('li', {}, 'Click your hens to collect their eggs.'),
      h('li', {}, 'Buy nesting boxes, pick breeds, and upgrade your farm.'),
      h('li', {}, 'Hire a rooster at 5 hens — click him to crow.'),
      h('li', {}, 'Stage 1: bank $1,000,000. Then the long game begins.')),
    h('div', { class: 'actions' }, h('button', { class: 'btn', type: 'button', onclick: start }, 'Start farming!')),
  ), { dismissible: false });
}

export function showWelcomeBack(r: OfflineReport): void {
  const s = game.state;
  const hours = (ms: number) => formatDuration(ms);
  const framing = !r.capped ? `You were away for ${hours(r.awayMs)}.`
    : r.awayMs > 86_400_000 ? `Your hens slept through the day. Earnings capped at ${hours(r.capMs)}.`
      : `Your hens needed rest — earnings capped at ${hours(r.capMs)}.`;
  const top = r.perBreed.slice(0, 3).filter((b) => b.earned > 0);
  const details = h('details', {},
    h('summary', {}, 'Show details'),
    h('div', {},
      ...r.perFlock.filter((f) => f.earned > 0).map((f) => h('div', { class: 'kv' }, h('span', {}, f.name), h('span', {}, formatMoney(f.earned)))),
      ...r.perBreed.slice(0, 8).map((b) => h('div', { class: 'kv' }, h('span', {}, HEN_BREEDS[b.breed].name), h('span', {}, formatMoney(b.earned)))),
      h('div', { class: 'kv' }, h('span', {}, 'Time credited'), h('span', {}, hours(r.creditedMs))),
      r.drainedMs > 0 ? h('div', { class: 'kv' }, h('span', {}, 'Combo time bank used'), h('span', {}, `+${hours(r.drainedMs)}`)) : null,
      h('div', { class: 'kv' }, h('span', {}, 'Offline cap'), h('span', {}, `${hours(offlineCapMs(s, game.ctx()))} (+2h per rooster)`)),
    ));
  openModal(h('div', {},
    h('h2', { text: '🐔 Welcome back!' }),
    h('p', { text: framing }),
    h('p', {}, 'Earned: ', h('b', {}, formatMoney(r.earned)), r.eggs ? ` from ${r.eggs.toLocaleString('en-US')} gathered eggs.` : '.'),
    r.noGathering ? h('p', { class: 'hint', text: 'Nobody gathered eggs while you were away. Buy an Egg Basket in the Farm tab and your hens earn even when you are gone.' }) : null,
    top.length ? h('p', { class: 'meta', text: `Top earners: ${top.map((t) => HEN_BREEDS[t.breed].name).join(', ')}.` }) : null,
    details,
    h('div', { class: 'actions' }, h('button', { class: 'btn', type: 'button', onclick: () => closeModal() }, 'Back to the farm')),
  ));
}

export function showWin(): void {
  const s = game.state;
  track({ type: 'win', atSec: Math.round(s.stats.activePlaySec) });
  openModal(h('div', {},
    h('h2', { text: '🏆 Stage 1 cleared. The long game begins.' }),
    h('p', {}, `You banked ${formatMoney(s.money)} with a full flock after ${formatDuration(s.stats.activePlaySec * 1000)} of play. Hen House Champion!`),
    h('p', { text: 'From here the game slows to an idle pace. Each new rooster opens a new flock with 15 nests — and takes real hours of gathering to afford.' }),
    h('ul', {},
      h('li', {}, `Rooster #2 costs ${formatMoney(ROOSTER_COSTS[1])}; the tenth, ${formatMoney(ROOSTER_COSTS[9])}.`),
      h('li', {}, 'Your farm earns while you are away: 2 hours, plus 2 more per rooster (22 hours at ten roosters).'),
      h('li', {}, 'Click combos bank extra offline time.'),
      h('li', {}, 'New gathering tech and six challenges just unlocked.')),
    h('div', { class: 'actions' },
      h('button', { class: 'btn secondary', type: 'button', onclick: () => { closeModal(); setTab('goals'); } }, 'See challenges'),
      h('button', { class: 'btn', type: 'button', onclick: () => closeModal() }, 'Keep farming')),
  ));
}

let achievementBatch: string[] = [];

function flushAchievements(): void {
  const ids = achievementBatch;
  achievementBatch = [];
  const defs = ids.map((id) => ACHIEVEMENTS.find((x) => x.id === id)).filter((a): a is (typeof ACHIEVEMENTS)[number] => !!a);
  if (defs.length === 1) toast(defs[0]!.flavor, 'achievement', `🏅 ${defs[0]!.name}`, 6000);
  else if (defs.length > 1) toast(defs.map((a) => a.name).join(' · '), 'achievement', `🏅 ${defs.length} achievements`, 6000);
}

/** Routes messages from the core outbox to toasts, canvas effects and modals. */
export function handleMessage(m: GameMessage): void {
  switch (m.kind) {
    case 'toast':
      toast(m.text, m.tone === 'warn' ? 'warn' : m.tone === 'good' ? 'good' : 'info');
      break;
    case 'achievement':
      // Achievements often land in bursts; show one toast per burst.
      if (achievementBatch.length === 0) window.setTimeout(flushAchievements, 300);
      achievementBatch.push(m.id);
      track({ type: 'achievement', id: m.id });
      break;
    case 'breedUnlocked':
      toast(`${HEN_BREEDS[m.breed].name} unlocked! Find her in the Hens tab.`, 'good', 'New breed');
      break;
    case 'win':
      showWin();
      break;
    case 'challengeTier':
      toast(`Challenge reached: ${m.label}`, 'good', '⭐ Challenge');
      break;
    case 'broodResult':
      if (m.success) {
        const traits = m.traits.map((t) => TRAITS[t].name).join(' + ');
        toast(`${HEN_BREEDS[m.breed].name} hatched a brood egg worth ${formatMoney(m.reward)}${traits ? ` and became a matriarch: ${traits}` : ''}.`, 'good', '🪺 Brood hatched');
      } else {
        toast(`${HEN_BREEDS[m.breed].name}'s brood didn't take. +${formatMoney(m.reward)} for the trouble.`, 'info');
      }
      break;
    case 'roosterHatched':
      toast(`A cockerel hatched! Your next rooster is ${m.discountPct}% off.`, 'good', '🐣 Rooster egg');
      break;
    case 'eventCollected':
      if (m.amount > 0) fieldFx(m.flockId, m.x, m.y, `+${formatMoney(m.amount)}`);
      break;
    case 'coins':
      fieldFx(m.flockId, m.x, m.y, `+${formatMoney(m.amount)}`);
      break;
  }
}
